import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Legend, Tooltip } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Legend, Tooltip);

export interface LineChartSeries {
    name: string;
    color: string;
    data: number[];
}

/** Gráfico de línea multi-serie con ejes y leyenda propia (a diferencia de
 * SparklineComponent, que oculta ejes/leyenda) — usado para "Evolución del
 * gasto en mantenimiento" (4 series) y "Tendencia mensual del gasto" (1
 * serie), reutilizando el mismo componente en vez de dos bespoke. */
@Component({
    selector: 'app-line-chart',
    template: `
        <div class="lc-legend" *ngIf="series.length > 1">
            <span class="lc-legend-item" *ngFor="let s of series">
                <span class="lc-legend-dot" [style.background]="s.color"></span>
                {{s.name}}
            </span>
        </div>
        <div class="lc-canvas-box"><canvas #canvas></canvas></div>
    `,
    styles: [`
        :host { display: block; width: 100%; }
        .lc-legend { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 12px; font-family: 'Inter', sans-serif; font-size: 13px; color: #364153; }
        .lc-legend-item { display: inline-flex; align-items: center; gap: 6px; }
        .lc-legend-dot { width: 8px; height: 8px; border-radius: 999px; display: inline-block; }
        .lc-canvas-box { position: relative; width: 100%; height: 260px; }
        canvas { width: 100% !important; height: 100% !important; }
    `]
})
export class LineChartComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() labels: string[] = [];
    @Input() series: LineChartSeries[] = [];
    @Input() currency = true;

    @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

    private chart: Chart<'line'> | null = null;

    ngAfterViewInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['labels'] && !changes['series']) return;
        this.render();
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private formatValue(value: number): string {
        if (!this.currency) return String(value);
        if (Math.abs(value) >= 1000) return '$' + Math.round(value / 1000) + 'k';
        return '$' + value;
    }

    private render(): void {
        const canvas = this.canvasRef?.nativeElement;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const datasets = this.series.map(s => ({
            label: s.name,
            data: s.data,
            borderColor: s.color,
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.35,
            fill: false,
        }));

        if (this.chart) {
            this.chart.data.labels = this.labels;
            this.chart.data.datasets = datasets as any;
            this.chart.update();
            return;
        }

        this.chart = new Chart(ctx, {
            type: 'line',
            data: { labels: this.labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (item) => `${item.dataset.label}: ${this.formatValue(Number(item.parsed.y))}`
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#98a2b3', font: { family: 'Inter', size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: '#f2f4f7' },
                        ticks: { color: '#98a2b3', font: { family: 'Inter', size: 11 }, callback: (v) => this.formatValue(Number(v)) }
                    }
                }
            }
        });
    }
}
